// src/encounter/services/use-organisation-features.ts
// Per-org feature flags that gate encounter capabilities (PROD parity — Flutter
// `GetOrganisationFeatureUseCase` → `PermissionDataSource.getOrganisationFeatures`).
//
// Wire (Flutter `permission_data_source_impl.dart:24`):
//
//   GET /api/Organization/{organizationId}/Features
//        → OrganisationFeature { web: FeaturesInfo, mobile: FeaturesInfo }
//        (404 → null, Flutter parity — no explicit flags means defaults apply)
//
// `current` is the platform-appropriate branch: web here (Flutter uses `kIsWeb ? web : mobile`).
// JSON keys are snake_case under `encounter` (`drug_interaction`, `ros`, `pe`) — mapped 1:1 with the
// Flutter `EncounterModule.fromJson`.
import { useQuery } from '@tanstack/react-query';
import { get, ApiError } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';

/** The `encounter` feature branch (Flutter `EncounterModule`). */
export interface EncounterFeatureFlags {
  ros?: boolean | null;
  pe?: boolean | null;
  /** Wire `drug_interaction`. */
  drugInteraction?: boolean | null;
}

/** Platform feature branch (Flutter `FeaturesInfo`). */
export interface OrganisationFeatureFlags {
  encounter?: EncounterFeatureFlags | null;
}

interface EncounterModuleWire {
  ros?: boolean | null;
  pe?: boolean | null;
  drug_interaction?: boolean | null;
}
interface FeaturesInfoWire {
  encounter?: EncounterModuleWire | null;
}
interface OrganisationFeatureWire {
  web?: FeaturesInfoWire | null;
  mobile?: FeaturesInfoWire | null;
}

function toFeatures(info: FeaturesInfoWire | null | undefined): OrganisationFeatureFlags {
  const enc = info?.encounter;
  return {
    encounter: enc
      ? { ros: enc.ros ?? null, pe: enc.pe ?? null, drugInteraction: enc.drug_interaction ?? null }
      : null,
  };
}

/**
 * The org's `web` feature flags. Returns `null` on 404 (Flutter parity — the org has no explicit
 * feature record, so callers fall back to their per-flag defaults).
 */
export function useOrganisationFeatures(organizationId: string | null | undefined) {
  return useQuery<OrganisationFeatureFlags | null>({
    queryKey: ['organisation-features', organizationId],
    queryFn: async ({ signal }) => {
      try {
        const res = await get<OrganisationFeatureWire>(
          `/api/Organization/${organizationId}/Features`,
          { signal },
        );
        // React is a web surface — use the `web` branch (Flutter `current = kIsWeb ? web : mobile`).
        return toFeatures(res?.web);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 30, // feature flags rarely change within a session
  });
}

/**
 * Whether the inline drug-interaction engine should run for the current org/user — Flutter
 * `PatientEncounterBloc.isDrugInteractionEnable`:
 *
 *   (organisationFeature.current.encounter.drugInteraction ?? false)
 *     && (userData.medicationSystem == null || medicationSystem.toLowerCase() == "vidal")
 *
 * The org flag defaults to FALSE when absent (unlike ros/pe which default true), so the engine is
 * off unless a clinic explicitly turns it on AND is on a Vidal-coded (or unset) medication system.
 */
export function useIsDrugInteractionEnabled(): boolean {
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const medicationSystem = useAuthStore((s) => s.user?.medicationSystem);
  const { data } = useOrganisationFeatures(organizationId);

  const orgFlagOn = data?.encounter?.drugInteraction ?? false;
  const medSystemOk = medicationSystem == null || medicationSystem.toLowerCase() === 'vidal';
  return orgFlagOn && medSystemOk;
}
